// The vendor picker on the kit's Picker, for the screens that had a plain
// select of vendors (13 October 2026). The audit and evidence screens keep
// components/VendorPicker.tsx, which says when its list could not be read and
// reads it again; that one was reviewed on its own and is not on the kit's
// Picker yet. Two pickers of vendors is one too many: this one should take
// over the other's unread state and then replace it.
import { useEffect, useMemo, useState, type JSX } from 'react'
import { api, type Vendor } from '../lib/api'
import { Picker, pickerMatches, type PickerOption } from './kit'

// Choosing one vendor by typing part of its name or code.
//
// The Reports screen used to ask for a vendor's identifier in a text box
// (observation 3.17 of 17 September 2026), which nobody outside the database
// knows. This is the same choice made the way a person makes it: type a few
// letters, pick the name. The value it hands back is still the identifier,
// because that is what the report filters on.
//
// It is the kit's Picker over the vendor list, so a vendor is chosen the same
// way on every screen that asks for one, and the same way a question or a
// document is chosen (11 October 2026: five forms asked for a vendor with a
// native select of every vendor in the tenant). What the box holds and what a
// key does to it are the kit's (pickerStep, pickerBox), given back from here
// under the names the Evidence screen's tests know them by.
//
// # THE CHOICE THAT IS NO VENDOR
//
// The Evidence screen's scope was a native select with "Every vendor in this
// tenant" above one option per vendor, which is a list nobody can use at three
// hundred vendors (the founder's review of 11 October 2026). A caller that
// passes `everyone` gets that choice here: it is what the box says while no
// vendor is chosen, and the first thing the list offers.

export { PICKER_AT_REST, heldBackNote, pickerBox, pickerStep, type PickerEvent, type PickerState } from './kit'

const SHOWN = 8

// The words a vendor is listed under, and the ones it is found by. A vendor
// trading under another name is found by either.
type Named = Pick<Vendor, 'id' | 'vendorCode' | 'legalName' | 'displayName'>

function option(v: Named): PickerOption {
  return { value: v.id, label: v.displayName || v.legalName, code: v.vendorCode, also: [v.displayName, v.legalName] }
}

// One row of the list. An empty id is the choice that is no vendor.
export type VendorOption = { id: string; name: string; code: string }

// Everything that answers to what has been typed: vendors whose name or code
// holds it. A vendor trading under another name is found by either. The list
// draws the first few of these and says how many it left out.
//
// The every-vendor choice is offered only while nothing is typed. It used to be
// offered whenever the letters typed were somewhere in its own words, first in
// the list and so the one Enter picked: "ant", typed for a vendor, is in
// "tenant", and Enter chose the whole tenant (first review, 11 October 2026).
// Somebody who has typed is looking for a vendor.
//
// With nothing typed a filter's picker lists nobody: three hundred names is
// the list it replaced. A form's picker (`browse`) answers with every vendor
// then, so its list can be opened and read from the top as the select it
// replaced could; the box draws the first eight and says how many it left out.
export function vendorChoices(vendors: Named[], typed: string, everyone?: string, browse = false): VendorOption[] {
  return pickerMatches(vendors.map(option), typed, everyone, browse).map((o) => ({
    id: o.value,
    name: o.label,
    code: o.code ?? '',
  }))
}

export function VendorChoice({
  id,
  label,
  value,
  onChange,
  placeholder = 'Search by name or code',
  compact = false,
  options,
  everyone,
  onTyped,
  browse = false,
  bare = false,
  invalid = false,
  describedBy,
}: {
  id: string
  label: string
  value: string
  onChange: (vendorId: string) => void
  placeholder?: string
  // In a row of controls the label is read by a screen reader and not drawn,
  // so the box lines up with the buttons beside it.
  compact?: boolean
  // The vendor list, when the screen already holds it. A screen with one
  // picker per row (the documents of an import) reads the list once and hands
  // it to every picker, rather than each picker reading it again. A form
  // hands over the list its select offered, so the picker offers exactly the
  // vendors the reader could choose before and no others.
  options?: Named[] | null
  // What an empty value means, in words, where it means something: "Every
  // vendor in this tenant". Absent, an empty value is nothing chosen yet.
  everyone?: string
  // Told whenever the letters in the box change, and with nothing when one is
  // chosen or they are cleared. Letters are not a choice: the value is still
  // what it was, and a screen about to act on the value can hold off while
  // the box shows a name nobody picked.
  onTyped?: (text: string) => void
  // The list opens on the vendors from the top before a letter is typed. For
  // a field that was a select; a filter waits for a letter, as it did.
  browse?: boolean
  // Only the control: the form keeps its own label and its own error line.
  bare?: boolean
  invalid?: boolean
  describedBy?: string
}): JSX.Element {
  const [loaded, setLoaded] = useState<Vendor[] | null>(null)
  const [failed, setFailed] = useState(false)
  const given = options !== undefined
  const vendors = given ? options : loaded

  useEffect(() => {
    if (given) return
    let live = true
    api
      .vendors({})
      .then((p) => {
        if (live) setLoaded(p.vendors)
      })
      .catch(() => {
        if (live) setFailed(true)
      })
    return () => {
      live = false
    }
  }, [given])

  const choices = useMemo(() => (vendors ? vendors.map(option) : null), [vendors])

  return (
    <Picker
      id={id}
      label={label}
      value={value}
      onChange={onChange}
      onTyped={onTyped}
      options={choices}
      placeholder={failed ? 'The vendor list could not be read' : placeholder}
      disabled={failed}
      // A list that could not be read says so in the box, which the every-vendor
      // words would otherwise be drawn over.
      none={failed ? undefined : everyone}
      browse={browse}
      // The scope that is already in force opens to say so when the keyboard
      // arrives, as it did. A form's field does not.
      opensOnFocus={Boolean(everyone)}
      hint={everyone ? 'Type a name or a code to choose one vendor' : undefined}
      limit={SHOWN}
      noun="vendor"
      missing="A vendor not in this list"
      compact={compact}
      bare={bare}
      invalid={invalid}
      describedBy={describedBy}
    />
  )
}
